import { JOINT_LABELS, type PlaneKey, type Side, type Signal } from '@/domain/hs1'

/**
 * Der Kanalwähler — nach Gelenk geordnet, nicht nach Grösse.
 *
 * WARUM DIE ANDERE ORDNUNG ALS IM VERZEICHNIS. Das Verzeichnis führt seine
 * Reihen in der Reihenfolge der Rechnung: alle Winkel, dann alle Momente, dann
 * alle Leistungen. Das ist richtig, wenn man wissen will, was gerechnet wird.
 * Wer aber ein Diagramm aufbaut, fragt nicht „welche Momente gibt es?", sondern
 * „was hat das Knie?" — und dafür muss das Knie an einer Stelle stehen und
 * nicht über drei Listen verteilt.
 *
 * EINE TASTE IST EINE FAMILIE UND KEIN KANAL. „Knie · Moment" ist je nach
 * Seitenwahl ein Kanal oder zwei; je nach Ebenenwahl der sagittale oder der
 * frontale. Wer stattdessen jeden Kanal einzeln anböte, hätte für dieselbe
 * Auskunft die sechsfache Zahl an Tasten — und müsste bei jedem Seitenwechsel
 * alles neu anklicken.
 *
 * DIE TASTEN WERDEN ERZEUGT UND NICHT GEPFLEGT. Sie kommen aus demselben
 * Verzeichnis wie die Kurven. Eine handgeführte Tastenliste veraltet an dem Tag,
 * an dem jemand eine Grösse in den Kern einbaut und die Liste vergisst.
 */

/** Welche Seiten die Tasten belegen. */
export type SideChoice = 'R' | 'L' | 'B'

export const SIDE_CHOICES: { key: SideChoice; label: string }[] = [
  { key: 'R', label: 'Rechts' },
  { key: 'L', label: 'Links' },
  { key: 'B', label: 'Beide' },
]

/**
 * Grössen, deren Kanäle eine Ebene tragen.
 *
 * Der Rest — Gelenkleistung, Energie, Antriebsstrang — fasst die Ebenen bereits
 * zusammen und hat deshalb keine. Eine Ebenenwahl ändert an so einer Taste
 * nichts, und sie wird deswegen auch nicht abgeblendet.
 */
const PLANED = new Set(['A', 'V', 'C', 'M', 'X'])

export interface KeyParts {
  q: string
  joint: string | null
  plane: PlaneKey | null
  side: Side | null
}

/**
 * Zerlegt einen Kanalschlüssel.
 *
 * Die Regel ist absichtlich schmal: die letzte Stelle ist eine Seite, wenn dort
 * genau `R` oder `L` steht; die dritte ist eine Ebene, wenn die Grösse eine
 * führt; die zweite ist ein Gelenk, wenn es dafür eine Bezeichnung gibt. Alles
 * andere ist keins — `C|cop|R` ist der Druckmittelpunkt und kein Gelenk namens
 * „cop", und ohne diese letzte Prüfung stünde genau das im Wähler.
 */
export function parseKey(key: string): KeyParts {
  const p = key.split('|')
  const last = p[p.length - 1]
  const side = last === 'R' || last === 'L' ? (last as Side) : null
  const plane = PLANED.has(p[0]) && p.length === 4 ? (p[2] as PlaneKey) : null
  const joint = p.length >= 3 && JOINT_LABELS[p[1]] ? p[1] : null
  return { q: p[0], joint, plane, side }
}

export interface PickButton {
  id: string
  label: string
  unit: string
  /** Was diese Taste bei der aktuellen Seiten- und Ebenenwahl belegt. */
  keys: string[]
  /**
   * Der Zusatz aus der Bezeichnung des Kanals — „Knie rechts · Energie" gibt
   * „Energie" her. Nur gebraucht, wo zwei Tasten einer Gruppe gleich hiessen.
   */
  suffix?: string
  /**
   * Die Taste gibt es, aber nicht in dieser Ebene.
   *
   * Sie wird abgeblendet und nicht weggelassen: eine Taste, die beim Wechsel der
   * Ebene verschwindet und wiederkommt, lässt das Raster springen und den
   * Benutzer suchen.
   */
  dim: boolean
}

/**
 * Was eine Taste zusammenfasst.
 *
 * Bei einem Gelenk die GRÖSSE JE EINHEIT und nicht nur die Grösse: unter
 * „Gelenkleistung" führt der Kern die Leistung in Watt UND die Energie in
 * Joule. Eine Taste über beide belegte zwei Kanäle, die nie zusammen in eine
 * Spur gehören, und stünde nie ganz an oder ganz aus. Die Einheit steht ohnehin
 * an der Taste — damit sind die beiden auseinanderzuhalten.
 */
type Family = Map<string, { label: string; suffix?: string; unit: string; all: Signal[] }>

export interface PickGroup {
  id: string
  label: string
  buttons: PickButton[]
}

/**
 * Baut den Wähler aus dem Verzeichnis.
 *
 * Erst die Gelenke in der Reihenfolge, in der sie im Durchlauf auftauchen, dann
 * alles, was zu keinem Gelenk gehört — Bodenreaktion, Bodenkontakt,
 * Schwerpunkt, Antriebsstrang — unter der Gruppe, unter der es gerechnet wird.
 */
export function pickerGroups(signals: Signal[], side: SideChoice, plane: PlaneKey): PickGroup[] {
  const sides: Side[] = side === 'B' ? ['R', 'L'] : [side]
  const joints = new Map<string, Family>()
  const rest = new Map<string, Family>()

  for (const signal of signals) {
    const parts = parseKey(signal.key)
    const home = parts.joint ? joints : rest
    const bucket = parts.joint ?? signal.group
    let inner = home.get(bucket)
    if (!inner) home.set(bucket, (inner = new Map()))

    /*
      Was eine Taste zusammenfasst: bei einem Gelenk die GRÖSSE (alle Momente
      des Knies sind eine Taste), sonst der Kanal ohne seine Seite (die
      senkrechte Bodenkraft ist eine Taste für beide Füsse). Sonst stünde unter
      „Bodenreaktion" dreimal dasselbe, einmal je Richtung und Seite.
    */
    const family = parts.joint ? `${signal.group}|${signal.unit}` : familyOf(signal, parts)
    let entry = inner.get(family)
    if (!entry) {
      /* Am Gelenk heisst die Taste nach der Grösse — die Einheit steht klein
         daneben und trennt Leistung von Energie. Ausserhalb heisst sie nach dem
         Kanal, dem die Seite abgenommen wurde. */
      const label = parts.joint ? signal.group : family
      const tail = signal.label.split(' · ')
      inner.set(
        family,
        (entry = {
          label,
          suffix: parts.joint && tail.length > 1 ? tail[tail.length - 1] : undefined,
          unit: signal.unit,
          all: [],
        }),
      )
    }
    entry.all.push(signal)
  }

  const build = (
    id: string,
    label: string,
    inner: Family,
  ): PickGroup => ({
    id,
    label,
    buttons: [...inner].map(([family, entry]) => {
      const { label: name, suffix, unit, all } = entry
      const planed = all.some((s) => parseKey(s.key).plane !== null)
      const wanted = all.filter((s) => {
        const parts = parseKey(s.key)
        if (parts.side && !sides.includes(parts.side)) return false
        if (planed && parts.plane !== plane) return false
        return true
      })
      return {
        id: `${id}|${family}`,
        label: name,
        suffix,
        unit,
        keys: wanted.map((s) => s.key),
        dim: wanted.length === 0,
      }
    }),
  })

  return [
    ...[...joints].map(([joint, inner]) => build(joint, JOINT_LABELS[joint] ?? joint, inner)),
    ...[...rest].map(([group, inner]) => build(group, group, inner)),
  ].map(distinguish)
}

/**
 * Zwei Tasten mit demselben Namen auseinanderhalten.
 *
 * Der Kern führt Leistung und Energie unter EINER Gruppe — beide hiessen sonst
 * „Gelenkleistung", einmal in Watt und einmal in Joule. Die Einheit daneben
 * trennt sie zwar, aber zwei gleich benannte Tasten nebeneinander liest man als
 * Versehen. Wo es klemmt, wird der Zusatz aus der Bezeichnung des Kanals
 * genommen: „Knie rechts · Energie" gibt „Energie" her. Gibt der Name nichts
 * her, bleibt es beim Gruppennamen — die Einheit steht ohnehin daran.
 */
function distinguish(group: PickGroup): PickGroup {
  const seen = new Map<string, number>()
  for (const button of group.buttons) {
    seen.set(button.label, (seen.get(button.label) ?? 0) + 1)
  }
  if ([...seen.values()].every((n) => n === 1)) return group
  return {
    ...group,
    buttons: group.buttons.map((button) =>
      (seen.get(button.label) ?? 0) > 1 && button.suffix
        ? { ...button, label: button.suffix }
        : button,
    ),
  }
}

/**
 * Der Name einer Taste ausserhalb der Gelenke.
 *
 * Die Bezeichnung des Kanals trägt die Seite mit — „Senkrecht rechts". An einer
 * Taste, die beide Seiten belegt, wäre das falsch, also fällt sie weg.
 *
 * Sie steht nicht immer am Ende: „Druckmittelpunkt rechts, vorwärts" trägt sie
 * in der Mitte. Deshalb wird das Wort gesucht und nicht der Schluss abgeschnitten
 * — mit dem angehängten Komma, sonst bliebe eines mitten im Namen stehen.
 */
function familyOf(signal: Signal, parts: KeyParts): string {
  if (!parts.side) return signal.label
  return signal.label.replace(/\s+(rechts|links)\b,?/i, '').trim()
}
